import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Card, Form, Button, Badge, Alert, Row, Col, 
  ListGroup, Collapse, InputGroup, Spinner
} from 'react-bootstrap';
import { 
  FaUser, FaUserTie, FaHome, FaBuilding, FaCheckCircle,
  FaPlus, FaTimes, FaEdit, FaInfoCircle, FaSearch,
  FaBalanceScale
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import axiosInstance from '../../helpers/api';

interface Party {
  id: string;
  party_type: string;
  name: string;
  email?: string;
  phone?: string;
  role?: string;
  role_description?: string;
  company_name?: string;
  broker_mls_id?: string;
  color?: string;
  user_id?: string;
  is_company?: boolean;  // Flag to indicate if the party is a company
  signing_party?: string; // Name of the person signing on behalf of company
}

interface TransactionPartyAssignmentProps {
  transactionId: string;
  transactionType?: string;
  clientType?: string;  // 'buyer' or 'seller' - who the agent represents
  transactionStage?: string;
  existingParties?: Party[];
  onPartiesUpdated?: (parties: Party[]) => void;
  isReadOnly?: boolean;
}

const TransactionPartyAssignment: React.FC<TransactionPartyAssignmentProps> = ({
  transactionId,
  transactionType = 'purchase',
  clientType,
  existingParties = [],
  onPartiesUpdated,
  isReadOnly = false
}) => {
  const [parties, setParties] = useState<Party[]>([]);
  const initializedRef = useRef(false);
  const [editingParty, setEditingParty] = useState<string | null>(null);
  const [addingParty, setAddingParty] = useState(false);
  const [newParty, setNewParty] = useState<Partial<Party>>({
    party_type: transactionType === 'rental' ? 'landlord' : 'buyer',
    name: '',
    email: '',
    phone: '',
    company_name: '',
    broker_mls_id: '',
    signing_party: ''
  });

  // Lead search states
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  // Map role_description to signing_party when loading existing parties
  const mapPartyFromDatabase = (party: any): Party => {
    return {
      ...party,
      signing_party: party.role_description || '',
      color: partyTypes.find(pt => pt.id === party.party_type)?.color || '#6b7280'
    };
  };

  const partyTypes = [
    { id: 'buyer', name: 'Buyer', icon: FaUser, color: '#4ade80' },
    { id: 'seller', name: 'Seller', icon: FaHome, color: '#60a5fa' },
    { id: 'tenant', name: 'Tenant', icon: FaUser, color: '#22c55e' },
    { id: 'landlord', name: 'Landlord', icon: FaBuilding, color: '#3b82f6' },
    { id: 'buyer_agent', name: "Buyer's Agent", icon: FaUserTie, color: '#a78bfa' },
    { id: 'seller_agent', name: "Seller's Agent", icon: FaUserTie, color: '#a78bfa' },
    { id: 'broker', name: 'Broker', icon: FaBuilding, color: '#f59e0b' },
    { id: 'attorney', name: 'Attorney', icon: FaBalanceScale, color: '#8b5cf6' },
    { id: 'title_escrow', name: 'Title/Escrow', icon: FaBuilding, color: '#6b7280' },
    { id: 'other', name: 'Other', icon: FaUser, color: '#6b7280' }
  ];

  // Fetch current user's agent profile on mount
  useEffect(() => {
    const fetchAgentProfile = async () => {
      try {
        const response = await axiosInstance.get('/agent/commission-profile');
        if (response.data.success && response.data.data) {
          const agentProfile = response.data.data;
          return {
            name: agentProfile.agentName,
            email: agentProfile.email,
            company_name: agentProfile.brokerageName
          };
        }
      } catch (error) {
        console.error('Error fetching agent profile:', error);
      }
      return null;
    };

    const initializeParties = async () => {
      // Skip re-initialization if parties are already loaded (prevents loop on delete)
      if (initializedRef.current) return;
      initializedRef.current = true;

      // Initialize with existing parties or defaults
      if (existingParties.length > 0) {
        setParties(existingParties.map(p => mapPartyFromDatabase(p)));
      } else if (!isReadOnly) {  // Only auto-create for new transactions
        // Fetch agent profile for auto-assignment
        const agentProfile = await fetchAgentProfile();
        
        // Set default parties structure based on transaction type
        if (transactionType === 'listing') {
          const parties: Party[] = [
            { id: '1', party_type: 'seller', name: '', email: '', phone: '', color: '#60a5fa' }
          ];
          
          // Auto-add current user as seller's agent
          if (agentProfile) {
            const agentParty = {
              id: '2',
              party_type: 'seller_agent',
              name: agentProfile.name,
              email: agentProfile.email,
              phone: '',
              company_name: agentProfile.company_name,
              color: '#a78bfa'
            };
            parties.push(agentParty);
            
            // Save agent party to database
            try {
              const response = await axiosInstance.post(`/transactions/${transactionId}/parties`, {
                party_type: 'seller_agent',
                name: agentProfile.name,
                email: agentProfile.email || '',
                phone: '',
                company_name: agentProfile.company_name || ''
              });
              
              if (response.data.success && response.data.party) {
                // Update with real database ID
                agentParty.id = response.data.party.id;
              }
            } catch (error) {
              console.error('Failed to auto-save agent party:', error);
            }
          }
          
          setParties(parties);
        } else if (transactionType === 'rental') {
          const parties: Party[] = [
            { id: '1', party_type: 'landlord', name: '', email: '', phone: '', color: '#3b82f6' }
          ];
          
          // Auto-add current user as landlord's agent
          if (agentProfile) {
            const agentParty = {
              id: '2',
              party_type: 'seller_agent', // Using seller_agent for landlord representation
              name: agentProfile.name,
              email: agentProfile.email,
              phone: '',
              company_name: agentProfile.company_name,
              color: '#a78bfa'
            };
            parties.push(agentParty);
            
            // Save agent party to database
            try {
              const response = await axiosInstance.post(`/transactions/${transactionId}/parties`, {
                party_type: 'seller_agent',
                name: agentProfile.name,
                email: agentProfile.email || '',
                phone: '',
                company_name: agentProfile.company_name || ''
              });
              
              if (response.data.success && response.data.party) {
                // Update with real database ID
                agentParty.id = response.data.party.id;
              }
            } catch (error) {
              console.error('Failed to auto-save agent party:', error);
            }
          }
          
          setParties(parties);
        } else if (transactionType === 'purchase') {
          // For purchase, determine agent role based on client_type
          const parties: Party[] = [];
          
          // Always start with a buyer
          parties.push({ id: '1', party_type: 'buyer', name: '', email: '', phone: '', color: '#4ade80' });
          
          // Auto-add current user as appropriate agent based on who they represent
          if (agentProfile) {
            // If clientType is 'seller', add as seller's agent, otherwise default to buyer's agent
            const agentType = clientType === 'seller' ? 'seller_agent' : 'buyer_agent';
            
            const agentParty = {
              id: '2',
              party_type: agentType,
              name: agentProfile.name,
              email: agentProfile.email,
              phone: '',
              company_name: agentProfile.company_name,
              color: '#a78bfa'
            };
            parties.push(agentParty);
            
            // Save agent party to database
            try {
              const response = await axiosInstance.post(`/transactions/${transactionId}/parties`, {
                party_type: agentType,
                name: agentProfile.name,
                email: agentProfile.email || '',
                phone: '',
                company_name: agentProfile.company_name || ''
              });
              
              if (response.data.success && response.data.party) {
                // Update with real database ID
                agentParty.id = response.data.party.id;
              }
            } catch (error) {
              console.error('Failed to auto-save agent party:', error);
            }
          }
          
          // If representing seller, also add a seller party placeholder
          if (clientType === 'seller') {
            parties.push({ id: '3', party_type: 'seller', name: '', email: '', phone: '', color: '#60a5fa' });
          }
          
          setParties(parties);
        } else if (transactionType === 'referral') {
          const parties: Party[] = [];
          
          // Auto-add current user as referring agent
          if (agentProfile) {
            const agentParty = {
              id: '1',
              party_type: 'buyer_agent',
              name: agentProfile.name,
              email: agentProfile.email,
              phone: '',
              company_name: agentProfile.company_name,
              color: '#a78bfa'
            };
            parties.push(agentParty);
            
            // Save agent party to database
            try {
              const response = await axiosInstance.post(`/transactions/${transactionId}/parties`, {
                party_type: 'buyer_agent',
                name: agentProfile.name,
                email: agentProfile.email || '',
                phone: '',
                company_name: agentProfile.company_name || ''
              });
              
              if (response.data.success && response.data.party) {
                // Update with real database ID
                agentParty.id = response.data.party.id;
              }
            } catch (error) {
              console.error('Failed to auto-save agent party:', error);
            }
          } else {
            parties.push({ id: '1', party_type: 'buyer_agent', name: '', email: '', phone: '', color: '#a78bfa' });
          }
          
          setParties(parties);
        }
      }
    };

    initializeParties();
  }, [existingParties, transactionType, clientType, isReadOnly, transactionId]);

  useEffect(() => {
    // Only update parent if callback provided and we have meaningful party data
    if (!onPartiesUpdated) return;
    
    const validParties = parties.filter(p => p.name);
    if (validParties.length > 0) {
      // Use a timeout to debounce rapid updates
      const timeoutId = setTimeout(() => {
        onPartiesUpdated(validParties);
      }, 500); // Increased debounce time to reduce calls
      
      return () => clearTimeout(timeoutId);
    }
  }, [parties, onPartiesUpdated]);

  // Click outside handler for search dropdown
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSearchDropdown(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchLeads = async (search: string) => {
    if (search.length < 2) return;
    
    setLoadingSearch(true);
    try {
      const response = await axiosInstance.get('/leads/search', {
        params: { search, limit: 5 }
      });
      
      if (response.data.leads) {
        setSearchResults(response.data.leads);
        setShowSearchDropdown(true);
      }
    } catch (error) {
      console.error('Error searching leads:', error);
    } finally {
      setLoadingSearch(false);
    }
  };

  const selectLead = (lead: any, partyType: string) => {
    const selectedPartyType = partyTypes.find(pt => pt.id === partyType);
    const party: Party = {
      id: Date.now().toString(),
      party_type: partyType,
      name: lead.lead_name || lead.name,
      email: lead.lead_email || lead.email || '',
      phone: lead.lead_phone || lead.phone || '',
      color: selectedPartyType?.color || '#6b7280',
      user_id: lead.user_id || undefined
    };

    setParties(prev => [...prev, party]);
    setSearchTerm('');
    setShowSearchDropdown(false);
  };

  // Debounced save — waits 800ms after the user stops typing before sending PUT
  const saveTimers = useRef<Record<string, NodeJS.Timeout>>({});

  const savePartyToServer = useCallback(async (partyId: string, updatedParty: Party, field: string, value: string) => {
    // Skip save if email is partial (not empty and not valid format)
    const emailVal = field === 'email' ? value : (updatedParty.email || '');
    if (emailVal && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailVal)) {
      return; // Wait for user to finish typing the email
    }

    try {
      const updateData: any = {
        party_type: updatedParty.party_type,
        name: updatedParty.name,
        email: emailVal,
        phone: updatedParty.phone || '',
        company_name: field === 'company_name' ? value : (updatedParty.company_name || ''),
        brokerage_mls_id: field === 'broker_mls_id' ? value : (updatedParty.broker_mls_id || ''),
        role_description: field === 'signing_party' ? value : (updatedParty.signing_party || '')
      };

      if (field !== 'signing_party' && field !== 'broker_mls_id' && field !== 'email') {
        updateData[field] = value;
      }

      console.log(`🔄 Saving party ${partyId} - ${field} = ${value}`);
      const response = await axiosInstance.put(`/transactions/${transactionId}/parties/${partyId}`, updateData);

      if (response.data.success) {
        toast.success('Changes saved', { duration: 1000, position: 'bottom-right' });
      }
    } catch (error: any) {
      console.error('❌ Error saving party:', error);
      toast.error('Failed to save changes');
    }
  }, [transactionId]);

  const handlePartyUpdate = (partyId: string, field: string, value: string) => {
    // Update local state immediately (responsive UI)
    setParties(prev => {
      const updated = prev.map(p =>
        p.id === partyId ? { ...p, [field]: value } : p
      );

      // Debounce the API call — only save after user stops typing
      const party = updated.find(p => p.id === partyId);
      if (party && !isTemporaryId(party.id)) {
        const timerKey = `${partyId}-${field}`;
        if (saveTimers.current[timerKey]) {
          clearTimeout(saveTimers.current[timerKey]);
        }
        saveTimers.current[timerKey] = setTimeout(() => {
          savePartyToServer(partyId, party, field, value);
          delete saveTimers.current[timerKey];
        }, 800);
      }

      return updated;
    });
  };
  
  // Helper to check if ID is temporary (frontend-generated)
  const isTemporaryId = (id: string): boolean => {
    return /^\d+$/.test(id) && id.length <= 15;
  };

  const handleAddParty = async () => {
    if (!newParty.name) {
      toast.error('Party name is required');
      return;
    }

    const partyType = partyTypes.find(pt => pt.id === newParty.party_type);
    
    try {
      // Save the party immediately to the database
      const response = await axiosInstance.post(`/transactions/${transactionId}/parties`, {
        party_type: newParty.party_type || 'buyer',
        name: newParty.name,
        email: newParty.email || '',
        phone: newParty.phone || '',
        company_name: newParty.company_name || '',
        brokerage_mls_id: newParty.broker_mls_id || '',
        role_description: newParty.signing_party || ''  // Map signing_party to role_description
      });

      if (response.data.success && response.data.party) {
        // Add the saved party with its real database ID
        const savedParty: Party = {
          ...response.data.party,
          color: partyType?.color || '#6b7280',
          signing_party: response.data.party.role_description || '' // Map back for UI
        };
        
        setParties(prev => [...prev, savedParty]);
        toast.success('Party added and saved');
        
        // Reset the form
        setAddingParty(false);
        setNewParty({ 
          party_type: transactionType === 'rental' ? 'landlord' : 'buyer', 
          name: '', 
          email: '', 
          phone: '', 
          company_name: '', 
          broker_mls_id: '',
          signing_party: ''
        });
        setSearchTerm('');
      } else {
        throw new Error('Failed to save party');
      }
    } catch (error: any) {
      console.error('❌ Error adding party:', error);
      toast.error(`Failed to add party: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleRemoveParty = async (partyId: string) => {
    try {
      // Find the party to check if it has a real database ID
      const party = parties.find(p => p.id === partyId);
      
      if (!party) {
        toast.error('Party not found');
        return;
      }

      // If it's a temporary party (numeric ID from Date.now()), just remove from UI
      if (isTemporaryId(party.id)) {
        setParties(prev => prev.filter(p => p.id !== partyId));
        toast.success('Party removed');
        return;
      }

      console.log('🗑️ Deleting party from database:', {
        partyId,
        transactionId,
        partyName: party.name
      });

      // Delete from database
      const response = await axiosInstance.delete(`/transactions/${transactionId}/parties/${partyId}`);
      
      if (response.data.success) {
        // Remove from UI after successful deletion
        setParties(prev => prev.filter(p => p.id !== partyId));
        toast.success('Party removed successfully');
      } else {
        throw new Error(response.data.error || 'Failed to delete party');
      }
    } catch (error: any) {
      console.error('❌ Error deleting party:', error);
      toast.error(`Failed to delete party: ${error.response?.data?.message || error.message}`);
    }
  };

  return (
    <Card className="mb-4">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">
            <FaUser className="me-2" />
            Transaction Parties
          </h5>
          <Button
            size="sm"
            variant="primary"
            onClick={() => setAddingParty(!addingParty)}
            disabled={isReadOnly}
          >
            {addingParty ? <FaTimes className="me-1" /> : <FaPlus className="me-1" />}
            {addingParty ? 'Cancel' : 'Add Party'}
          </Button>
        </div>

        {/* Existing Parties */}
        <ListGroup className="mb-3">
          {parties.map((party) => {
            const partyType = partyTypes.find(pt => pt.id === party.party_type);
            const Icon = partyType?.icon || FaUser;
            
            return (
              <ListGroup.Item key={party.id} className="d-flex justify-content-between align-items-start">
                <div className="flex-grow-1">
                  <div className="d-flex align-items-center mb-2">
                    <Icon className="me-2" style={{ color: party.color }} />
                    {editingParty === party.id ? (
                      <>
                        <Row className="g-2 flex-grow-1">
                          <Col md={4}>
                            <Form.Select
                              size="sm"
                              value={party.party_type}
                              onChange={(e) => handlePartyUpdate(party.id, 'party_type', e.target.value)}
                            >
                              {partyTypes.map(pt => (
                                <option key={pt.id} value={pt.id}>{pt.name}</option>
                              ))}
                            </Form.Select>
                          </Col>
                          <Col md={8}>
                            <Form.Control
                              size="sm"
                              type="text"
                              placeholder="Name"
                              value={party.name}
                              onChange={(e) => handlePartyUpdate(party.id, 'name', e.target.value)}
                            />
                          </Col>
                        </Row>
                        <Row className="mt-1">
                          <Col md={6}>
                            <Form.Control
                              size="sm"
                              type="email"
                              placeholder="Email"
                              value={party.email}
                              onChange={(e) => handlePartyUpdate(party.id, 'email', e.target.value)}
                            />
                          </Col>
                          <Col md={6}>
                            <Form.Control
                              size="sm"
                              type="tel"
                              placeholder="Phone"
                              value={party.phone}
                              onChange={(e) => handlePartyUpdate(party.id, 'phone', e.target.value)}
                            />
                          </Col>
                        </Row>
                        {/* Company Name Row - Now available for all party types */}
                        <Row className="mt-1">
                          <Col md={party.party_type === 'buyer_agent' || party.party_type === 'seller_agent' ? 8 : 12}>
                            <Form.Control
                              size="sm"
                              placeholder={
                                party.party_type === 'title_escrow' ? 'Title/Escrow Company' : 
                                party.party_type === 'buyer' || party.party_type === 'seller' ? 'Company/Organization (if applicable)' :
                                party.party_type === 'tenant' || party.party_type === 'landlord' ? 'Company/Property Management (if applicable)' :
                                party.party_type === 'attorney' ? 'Law Firm' :
                                'Company/Brokerage'
                              }
                              value={party.company_name || ''}
                              onChange={(e) => handlePartyUpdate(party.id, 'company_name', e.target.value)}
                            />
                          </Col>
                          {(party.party_type === 'buyer_agent' || party.party_type === 'seller_agent') && (
                            <Col md={4}>
                              <Form.Control
                                size="sm"
                                placeholder="Broker MLS ID"
                                value={party.broker_mls_id || ''}
                                onChange={(e) => handlePartyUpdate(party.id, 'broker_mls_id', e.target.value)}
                              />
                            </Col>
                          )}
                        </Row>
                        
                        {/* Signing Party Row - For companies */}
                        {party.company_name && (party.party_type === 'buyer' || party.party_type === 'seller' || party.party_type === 'tenant' || party.party_type === 'landlord') && (
                          <Row className="mt-1">
                            <Col>
                              <Form.Control
                                size="sm"
                                placeholder="Authorized Signer Name (if different from above)"
                                value={party.signing_party || ''}
                                onChange={(e) => handlePartyUpdate(party.id, 'signing_party', e.target.value)}
                              />
                              <Form.Text className="text-muted">
                                Person who will sign on behalf of {party.company_name}
                              </Form.Text>
                            </Col>
                          </Row>
                        )}
                      </>
                    ) : (
                      <div>
                        <strong>{partyType?.name}:</strong> {party.name || '(Not set)'}
                        {party.company_name && (
                          <span className="ms-2 text-info">
                            {party.party_type === 'title_escrow' ? `at ${party.company_name}` : 
                             party.party_type === 'attorney' ? `with ${party.company_name}` :
                             party.party_type === 'buyer' || party.party_type === 'seller' || party.party_type === 'tenant' || party.party_type === 'landlord' ? `(${party.company_name})` :
                             `with ${party.company_name}`}
                          </span>
                        )}
                        {party.signing_party && party.company_name && (
                          <div className="small text-muted ms-3">
                            Signing Party: {party.signing_party}
                          </div>
                        )}
                        {party.broker_mls_id && (party.party_type === 'buyer_agent' || party.party_type === 'seller_agent') && (
                          <Badge bg="info" className="ms-2">MLS: {party.broker_mls_id}</Badge>
                        )}
                        {party.email && <Badge bg="secondary" className="ms-2">{party.email}</Badge>}
                        {party.phone && <Badge bg="secondary" className="ms-2">{party.phone}</Badge>}
                      </div>
                    )}
                  </div>
                </div>
                <div className="ms-2">
                  <Button
                    size="sm"
                    variant="outline-primary"
                    onClick={() => setEditingParty(editingParty === party.id ? null : party.id)}
                    className="me-1"
                    disabled={isReadOnly}
                  >
                    {editingParty === party.id ? <FaCheckCircle /> : <FaEdit />}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-danger"
                    onClick={() => handleRemoveParty(party.id)}
                    disabled={isReadOnly}
                  >
                    <FaTimes />
                  </Button>
                </div>
              </ListGroup.Item>
            );
          })}
        </ListGroup>

        {/* Add Party Form */}
        <Collapse in={addingParty}>
          <Card className="mt-3">
            <Card.Body>
              <h6 className="mb-3">Add New Party</h6>
              
              {/* Search Existing Leads */}
              <div className="mb-3" ref={searchRef}>
                <InputGroup>
                  <InputGroup.Text>
                    <FaSearch />
                  </InputGroup.Text>
                  <Form.Control
                    placeholder="Search existing leads/contacts..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      searchLeads(e.target.value);
                    }}
                  />
                  {loadingSearch && (
                    <InputGroup.Text>
                      <Spinner animation="border" size="sm" />
                    </InputGroup.Text>
                  )}
                </InputGroup>
                
                {showSearchDropdown && searchResults.length > 0 && (
                  <ListGroup className="position-absolute w-100 mt-1" style={{ zIndex: 1000 }}>
                    {searchResults.map((lead) => (
                      <ListGroup.Item 
                        key={lead.id}
                        action
                        onClick={() => selectLead(lead, newParty.party_type || 'buyer')}
                      >
                        <div className="d-flex justify-content-between">
                          <div>
                            <strong>{lead.lead_name || lead.name}</strong>
                            <br />
                            <small className="text-muted">
                              {lead.lead_email || lead.email} | {lead.lead_phone || lead.phone}
                            </small>
                          </div>
                          <Badge bg="primary">Select</Badge>
                        </div>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                )}
              </div>

              <Alert variant="info" className="mb-3">
                <FaInfoCircle className="me-2" />
                Or manually enter party information:
              </Alert>

              {/* Manual Entry Form */}
              <Row className="mb-2">
                <Col md={4}>
                  <Form.Label className="small mb-1">Party Type</Form.Label>
                  <Form.Select
                    value={newParty.party_type}
                    onChange={(e) => setNewParty(prev => ({ ...prev, party_type: e.target.value }))}
                  >
                    {partyTypes.map(pt => (
                      <option key={pt.id} value={pt.id}>{pt.name}</option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={8}>
                  <Form.Label className="small mb-1">Name *</Form.Label>
                  <Form.Control
                    placeholder="Full name"
                    value={newParty.name}
                    onChange={(e) => setNewParty(prev => ({ ...prev, name: e.target.value }))}
                  />
                </Col>
              </Row>

              <Row className="mb-2">
                <Col md={6}>
                  <Form.Label className="small mb-1">Email</Form.Label>
                  <Form.Control
                    type="email"
                    placeholder="email@example.com"
                    value={newParty.email}
                    onChange={(e) => setNewParty(prev => ({ ...prev, email: e.target.value }))}
                  />
                </Col>
                <Col md={6}>
                  <Form.Label className="small mb-1">Phone</Form.Label>
                  <Form.Control
                    type="tel"
                    placeholder="(555) 123-4567"
                    value={newParty.phone}
                    onChange={(e) => setNewParty(prev => ({ ...prev, phone: e.target.value }))}
                  />
                </Col>
              </Row>

              {/* Company Name Row - Available for all party types */}
              <Row className="mt-2">
                <Col md={newParty.party_type === 'buyer_agent' || newParty.party_type === 'seller_agent' ? 8 : 12}>
                  <Form.Label className="small mb-1">
                    {newParty.party_type === 'title_escrow' ? 'Title/Escrow Company' : 
                     newParty.party_type === 'buyer' || newParty.party_type === 'seller' ? 'Company/Organization (optional)' :
                     newParty.party_type === 'tenant' || newParty.party_type === 'landlord' ? 'Company/Property Management (optional)' :
                     newParty.party_type === 'attorney' ? 'Law Firm' :
                     'Company/Brokerage'}
                  </Form.Label>
                  <Form.Control
                    placeholder={
                      newParty.party_type === 'title_escrow' ? "e.g., First American Title, Chicago Title" : 
                      newParty.party_type === 'buyer' || newParty.party_type === 'seller' ? "e.g., ABC Corp LLC, XYZ Holdings" :
                      newParty.party_type === 'attorney' ? "e.g., Smith & Associates" :
                      "e.g., HomeSmart, Keller Williams"
                    }
                    value={newParty.company_name}
                    onChange={(e) => setNewParty(prev => ({ ...prev, company_name: e.target.value }))}
                  />
                </Col>
                {(newParty.party_type === 'buyer_agent' || newParty.party_type === 'seller_agent') && (
                  <Col md={4}>
                    <Form.Label className="small mb-1">Broker MLS ID</Form.Label>
                    <Form.Control
                      placeholder="e.g., 12345"
                      value={newParty.broker_mls_id || ''}
                      onChange={(e) => setNewParty(prev => ({ ...prev, broker_mls_id: e.target.value }))}
                    />
                  </Col>
                )}
              </Row>
              
              {/* Signing Party Row - For companies */}
              {newParty.company_name && (newParty.party_type === 'buyer' || newParty.party_type === 'seller' || newParty.party_type === 'tenant' || newParty.party_type === 'landlord') && (
                <Row className="mt-2">
                  <Col>
                    <Form.Label className="small mb-1">Authorized Signer (if different from name above)</Form.Label>
                    <Form.Control
                      placeholder="Name of person who will sign on behalf of the company"
                      value={newParty.signing_party || ''}
                      onChange={(e) => setNewParty(prev => ({ ...prev, signing_party: e.target.value }))}
                    />
                    <Form.Text className="text-muted">
                      Leave blank if the name above is the actual signer
                    </Form.Text>
                  </Col>
                </Row>
              )}
              
              <div className="mt-2">
                <Button size="sm" variant="success" onClick={handleAddParty} className="me-2">
                  <FaCheckCircle className="me-1" />
                  Add
                </Button>
                <Button size="sm" variant="secondary" onClick={() => setAddingParty(false)}>
                  Cancel
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Collapse>

        {/* Show auto-save status */}
        <div className="text-muted text-center mt-3">
          <small>
            <FaCheckCircle className="me-1 text-success" />
            All changes are saved automatically
          </small>
        </div>
      </Card.Body>
    </Card>
  );
};

export default TransactionPartyAssignment;